import React, { createContext, useState, useContext, ReactNode } from "react";
import Dealer from "../apiService/dealer/dealerApiService";
import { DealerType } from "../pages/UserManagementPage/components/dealer/types";

// ----------- Type Definitions -----------
interface DealerContextType {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;

  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;

  success: boolean;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;

  dealers: DealerType[];
  setDealers: React.Dispatch<React.SetStateAction<DealerType[]>>;

  dealersForGroup: DealerType[];
  setDealersForGroup: React.Dispatch<React.SetStateAction<DealerType[]>>;

  dealer: DealerType;
  setDealer: React.Dispatch<React.SetStateAction<DealerType>>;

  selectDealer: (dealerId: string) => void;
  getSelectedDealer: () => DealerType;

  getAllDealersForDataGrid: (params?: Record<string, any>) => Promise<void>;
  getAllDealersForDataGridByGroupId: (params?: Record<string, any>) => Promise<any>;
  addDealer: (dealer: any) => Promise<void>;
  deleteDealer: (dealerId: string) => Promise<void>;
  editDealer: (dealerId: string, data: any) => Promise<void>;
}

// ----------- Context Create -----------
const DealerContext = createContext<DealerContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const DealerProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [dealers, setDealers] = useState<any[]>([]);
  const [dealer, setDealer] = useState<any>({});
  const [dealersForGroup, setDealersForGroup] = useState<any>([]);
  const [selectedDealer, setSelectedDealer] = useState<any>({});

  // ----------- API Calls Using Dealer Service -----------
  const getAllDealersForDataGridByGroupId = async (params: Record<string, any> = {}) => {
    setLoading(true);
    setError("");
    try {
      const response = await Dealer.getAllDealersForDataGrid(params);
      return response.body.data;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch dealers");
    } finally {
      setLoading(false);
    }
  };

  // ----------- API Calls Using Dealer Service -----------
  const getAllDealersForDataGrid = async (params: Record<string, any> = {}) => {
    setLoading(true);
    setError("");
    try {
      const response = await Dealer.getAllDealersForDataGrid(params);
      setDealers(response.body.data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch dealers");
    } finally {
      setLoading(false);
    }
  };

  const addDealer = async (dealer: any) => {
    setLoading(true);
    setError("");
    try {
      await Dealer.addDealer(dealer);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add dealer");
    } finally {
      setLoading(false);
    }
  };

  const deleteDealer = async (dealerId: string) => {
    setLoading(true);
    setError("");
    try {
      await Dealer.deleteDealer(dealerId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to delete dealer");
    } finally {
      setLoading(false);
    }
  };

  const editDealer = async (dealerId: string, data: any) => {
    setLoading(true);
    setError("");
    try {
      await Dealer.editDealer(dealerId, data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to edit dealer");
    } finally {
      setLoading(false);
    }
  };

  const selectDealer = (dealerId: string) => {
    const selected = dealers.find((dealer) => dealer.id === dealerId);
    if (selected) {
      setSelectedDealer(selected);
    } else {
      console.warn("Dealer not found:", dealerId);
      setSelectedDealer({} as DealerType); // or handle accordingly
    }
  };

  const getSelectedDealer = (): any => {
    return selectedDealer;
  }

  return (
    <DealerContext.Provider
      value={{
        loading,
        setLoading,
        error,
        setError,
        success,
        setSuccess,
        dealers,
        setDealers,
        dealersForGroup,
        setDealersForGroup,
        dealer,
        setDealer,
        getAllDealersForDataGrid,
        getAllDealersForDataGridByGroupId,
        addDealer,
        deleteDealer,
        editDealer,
        selectDealer,
        getSelectedDealer,
      }}
    >
      {children}
    </DealerContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useDealer = (): DealerContextType => {
  const context = useContext(DealerContext);
  if (context === undefined) {
    throw new Error("useDealer must be used within a DealerProvider");
  }
  return context;
};
